(() => {
  'use strict';

  const INPUTS = '.mini-cart .qty-box-input[data-quantity-variant-id],.cart-page .qty-box-input[data-quantity-variant-id]';
  const LIMIT_ERROR = /only\s+(?:add\s+)?\d+|\d+\s+items?\s+was\s+added|due\s+to\s+availability|maximum\s+available/i;
  const cache = new Map();
  const pending = new Map();
  let timer;

  const num = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const itemFor = (input) => input.closest('.product-card-cart,.cart-item');
  const keyFor = (input) => `${input.closest('.mini-cart') ? 'drawer' : 'page'}:${input.dataset.quantityVariantId}:${input.dataset.index}`;

  const errorFor = (input) => {
    const item = itemFor(input);
    const text = item?.querySelector('.cart-pro-err-text');
    return { item, text };
  };

  const clearError = (input, force = false) => {
    const { item, text } = errorFor(input);
    if (!item || !text) return;
    const message = text.textContent.trim();
    if (force || item.dataset.zyvInventoryError === 'true' || !message || LIMIT_ERROR.test(message)) {
      text.textContent = '';
      item.removeAttribute('data-zyv-inventory-error');
    }
  };

  const showError = (input, maximum) => {
    const { item, text } = errorFor(input);
    if (!item || !text) return;
    text.textContent = `You can only add ${Math.max(0, Math.floor(maximum))} of this item to your cart.`;
    item.dataset.zyvInventoryError = 'true';
    text.closest('.cart-pro-err')?.setAttribute('role', 'alert');
  };

  const productUrlFor = (input) => {
    const link = itemFor(input)?.querySelector('.card-title[href],.item-title[href]');
    if (!link) return '';
    const url = new URL(link.href, location.origin);
    url.search = '';
    url.hash = '';
    return url.href;
  };

  const inventoryFor = (url) => {
    if (!url) return Promise.resolve(null);
    if (cache.has(url)) return cache.get(url);

    const request = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('#ZyviraProductInventory');
        if (!node?.textContent) return null;
        return new Map(JSON.parse(node.textContent).map((variant) => [String(variant.id), variant]));
      })
      .catch((error) => {
        cache.delete(url);
        console.warn('[Zyvira cart inventory]', error);
        return null;
      });

    cache.set(url, request);
    return request;
  };

  const variantCartQuantity = (source, variantId) => {
    const scope = source.closest('.mini-cart,.cart-page') || document;
    return [...scope.querySelectorAll('.qty-box-input[data-quantity-variant-id]')]
      .filter((input) => String(input.dataset.quantityVariantId) === String(variantId))
      .reduce((total, input) => total + Math.max(0, num(input.value)), 0);
  };

  const maximumFor = (input, variant) => {
    const current = Math.max(0, num(input.value));
    const ruleMax = variant.maximum == null ? Infinity : num(variant.maximum, Infinity);
    let stockMax = Infinity;

    if (variant.inventoryManagement && variant.inventoryPolicy === 'deny') {
      const total = variantCartQuantity(input, input.dataset.quantityVariantId);
      const otherLines = Math.max(0, total - current);
      stockMax = Math.max(current, Math.max(0, num(variant.inventoryQuantity)) - otherLines);
    }

    const maximum = Math.min(ruleMax, stockMax);
    return Number.isFinite(maximum) ? Math.max(0, Math.floor(maximum)) : Infinity;
  };

  const applyInventory = (input, variant) => {
    if (!variant) return;

    const minimum = Math.max(1, num(variant.minimum, num(input.min, 1)));
    const increment = Math.max(1, num(variant.increment, num(input.step, 1)));
    const maximum = maximumFor(input, variant);

    input.dataset.zyvInventoryReady = 'true';
    input.dataset.zyvMinimum = String(minimum);
    input.dataset.zyvIncrement = String(increment);
    input.dataset.zyvMaximum = Number.isFinite(maximum) ? String(maximum) : '';
    input.min = String(minimum);
    input.step = String(increment);
    if (Number.isFinite(maximum)) input.max = String(maximum);
    else input.removeAttribute('max');

    const attempt = pending.get(keyFor(input));
    const current = num(input.value, minimum);
    if (attempt) {
      current < attempt ? showError(input, current) : clearError(input, true);
      pending.delete(keyFor(input));
    } else if (itemFor(input)?.dataset.zyvInventoryError !== 'true') {
      clearError(input);
    }
  };

  const enhance = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      document.querySelectorAll(INPUTS).forEach((input) => {
        inventoryFor(productUrlFor(input)).then((variants) => {
          applyInventory(input, variants?.get(String(input.dataset.quantityVariantId)));
        });
      });
    }, 45);
  };

  const maximum = (input) => {
    if (input.dataset.zyvInventoryReady !== 'true' || input.dataset.zyvMaximum === '') return Infinity;
    return num(input.dataset.zyvMaximum, Infinity);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.qty-bt[name="plus"],.qty-bt[name="minus"]');
    if (!button) return;
    const input = button.closest('qty-box')?.querySelector('.qty-box-input');
    if (!input?.matches(INPUTS)) return;

    if (button.name === 'minus') {
      pending.delete(keyFor(input));
      clearError(input, true);
      return;
    }

    const requested = num(input.value) + Math.max(1, num(input.dataset.zyvIncrement, num(input.step, 1)));
    const limit = maximum(input);
    if (Number.isFinite(limit) && requested > limit) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showError(input, limit);
      return;
    }

    clearError(input, true);
    pending.set(keyFor(input), requested);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(INPUTS)) return;
    const requested = num(input.value, num(input.getAttribute('value'), 1));
    const limit = maximum(input);

    if (Number.isFinite(limit) && requested > limit) {
      event.preventDefault();
      event.stopImmediatePropagation();
      input.value = input.getAttribute('value') || String(Math.min(requested, limit));
      showError(input, limit);
      return;
    }

    clearError(input, true);
    pending.set(keyFor(input), requested);
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches(INPUTS) && num(input.value) <= maximum(input)) {
      clearError(input, true);
    }
  }, true);

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
      node instanceof Element && (node.matches('.cart-item,.js-contents,cart-pro,ajax-items') || node.querySelector('.qty-box-input[data-quantity-variant-id]'))
    ))) enhance();
  }).observe(document.body, { childList: true, subtree: true });

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', enhance, { once: true })
    : enhance();
})();
